"use client";
import { ArrowDown } from "@/components/icons/arrow";
import { Image } from "@/components/shared/image";
import Lightbox from "@/components/shared/ligthbox";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useLocalRouter from "@/hooks/use-router";
import { useWindowSize } from "@/hooks/use-window-size";
import { useTranslation } from "@/i18n/client";
import { Lang } from "@/i18n/settings";
import { cn } from "@/lib/utils";
import {
  getHonorees,
  HonoreesCategoriesProps,
  HonoreesProps,
} from "@/services/honorees/honorees.server";
import { MetaProps } from "@/types";
import { Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Categories } from "./categories";

const MAX_HEIGHT = 600;
const MIN_HEIGHT = 500;

type Props = {
  data: HonoreesProps[];
  meta?: MetaProps["pagination"];
  lang: Lang;
  searchParams: { year: string; category: string };
  categories: HonoreesCategoriesProps[];
  tabs: string[];
};

type HonoreesData = HonoreesProps & {
  height?: number;
  fill?: boolean;
  absolute?: boolean;
};

export function Cards({
  data: initialData,
  meta,
  lang,
  tabs,
  searchParams,
  categories: catRes,
}: Props) {
  const { t } = useTranslation(lang, "honorees");

  const [categories] = useState(catRes);

  const { width } = useWindowSize();

  const [index, setIndex] = useState(-1);
  const [data, setData] = useState<HonoreesData[][]>(convertArray(initialData));
  const [tempData, setTempData] = useState<HonoreesData[]>(
    mapToHonoreesProps(initialData),
  );
  const router = useLocalRouter();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState((meta?.pageCount ?? 1) > 1);

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  async function loadMore() {
    if (!hasMore || loading) return;

    setLoading(true);

    const res = await getHonorees({
      locale: lang,
      page: page + 1,
      year: searchParams?.year
        ? searchParams?.year === "All"
          ? undefined
          : Number(searchParams.year)
        : undefined,
      category: searchParams?.category,
    });

    if (res?.data) {
      const td = mapToHonoreesProps([...tempData, ...res.data]);
      setTempData(td);

      const d = convertArray([...data.flat(), ...res.data]);

      setData(d);
      setPage(page + 1);
      setHasMore(res.meta.pageCount > page + 1);
    }

    setLoading(false);
  }

  function convertArray(arr: HonoreesData[]): HonoreesData[][] {
    const result = [] as HonoreesData[][];
    const itemsPerChildArray = 4;

    for (let i = 0; i < arr.length; i += itemsPerChildArray) {
      const childArray = arr.slice(i, i + itemsPerChildArray);

      for (let j = 0; j < childArray.length; j++) {
        // Determine if the parent array index (i) is odd or even
        const isParentOdd = Math.floor(i / itemsPerChildArray) % 2 === 1;
        // Determine the height based on the child array index (j)
        if (isParentOdd) {
          // Parent index is odd
          childArray[j].height = j % 2 === 0 ? MIN_HEIGHT : MAX_HEIGHT;
          childArray[j].fill = j % 2 === 0;
          childArray[j].absolute = j % 2 === 1;
        } else {
          // Parent index is even
          childArray[j].height = j % 2 === 0 ? MAX_HEIGHT : MIN_HEIGHT;
        }
      }

      result.push(childArray);
    }

    return result;
  }

  function mapToHonoreesProps(items: HonoreesProps[]): HonoreesData[] {
    return items.map((item, index) => {
      const newItem = {
        ...item,
        height: index % 2 === 0 ? MAX_HEIGHT : MIN_HEIGHT,
      } as HonoreesProps & {
        height: number;
        fill?: boolean;
        absolute?: boolean;
      };

      if (index % 3 === 1) {
        if (newItem.height === MAX_HEIGHT) {
          newItem.absolute = true;
        } else {
          newItem.fill = true;
        }
      }

      return newItem as HonoreesProps & {
        height: number;
        fill?: boolean;
        absolute?: boolean;
      };
    });
  }

  useEffect(() => {
    if (page > 1) return;

    setTempData(mapToHonoreesProps(initialData));
    const d = convertArray(initialData);

    setData(d);
    setHasMore((meta?.pageCount ?? 1) > 1);
  }, [initialData, page]);

  return (
    <>
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
        <h1 className="text-3xl text-secondary md:flex-[0.3] xl:flex-[0.6] xl:text-4xl ltr:font-playfair">
          {t("pageTitle")}
        </h1>
        <div className="flex flex-1 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Categories
            onClick={() => {
              setPage(1);
            }}
            placeholder={t("allYears")}
            searchParams={searchParams}
            tabs={tabs}
          />
          <div className="flex max-w-[350px]">
            <Select
              onValueChange={(e) => {
                setPage(1);

                router.push({
                  search: {
                    category: e.startsWith("All") ? "" : e,
                  },
                });
              }}
              dir={lang === "en" ? "ltr" : "rtl"}
            >
              <SelectTrigger className="h-12 min-w-[130px] !rounded-[1000px] bg-secondary/10 px-4 text-secondary transition-all duration-150 ease-in">
                <SelectValue placeholder={t("allCategories")} />
              </SelectTrigger>
              <SelectContent
                avoidCollisions={false}
                align="end"
                className="overflow-y-auto bg-background text-secondary landscape:max-[800px]:max-h-[200px]"
              >
                <SelectItem value={"All"}>{t("allCategories")}</SelectItem>
                {categories?.map((category) => (
                  <SelectItem key={category?.title} value={category?.title}>
                    {category.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {isClient && (
        <>
          {tempData.length === 0 ? (
            <div className="flex h-full min-h-[500px] flex-1 items-center justify-center">
              {t("noDataFound")}.
            </div>
          ) : (
            <div className="flex h-full flex-col gap-12 sm:gap-20">
              {width > 1270 ? (
                <DesktopCards setIndex={setIndex} data={data} />
              ) : (
                <MobileCards setIndex={setIndex} data={tempData} />
              )}

              <div
                className={cn(
                  "flex items-center justify-center",
                  !hasMore && "hidden",
                )}
              >
                <Button
                  onClick={loadMore}
                  disabled={loading}
                  variant="outline"
                  className="flex h-12 max-w-fit gap-2 rounded-full border-secondary/30 px-8 font-normal uppercase text-secondary/70 opacity-80 transition-all duration-150 ease-in hover:border-secondary hover:text-secondary/90 lg:h-14 xl:text-base ltr:font-sans"
                >
                  {t("loadMore")}
                  <ArrowDown />
                </Button>
              </div>

              <Lightbox
                images={data.flat().map((item) => ({
                  src: item.image_link,
                  alt: item.name,
                  title: item?.name,
                  description: item?.description,
                }))}
                index={index}
                onClose={() => {
                  setIndex(-1);
                }}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}

function DesktopCards({
  data,
  setIndex,
}: {
  data: HonoreesData[][];
  setIndex: (index: number) => void;
}) {
  return (
    <div className="relative flex h-full flex-wrap gap-6 overflow-hidden">
      {data.map((item, itdx) => (
        <React.Fragment key={itdx}>
          {item.map((itm, idx) => (
            <div
              key={itm.id}
              className={`relative min-w-full flex-1 sm:min-w-[40%] sm:max-w-[49%] lg:min-w-[20%] lg:max-w-[25%]`}
            >
              <div
                className={cn(
                  "flex flex-col gap-4 rounded-[40px] bg-foreground p-4 pb-8 sm:h-[500px]",
                  itm.absolute ? "bottom-0 left-0 right-0 lg:absolute" : "",
                  !itm.description && "pb-4",
                )}
                style={{ height: `${itm.height}px` }}
              >
                <div
                  onClick={() => {
                    const itemActualIndex = itdx * 4 + idx;

                    setIndex(itemActualIndex);
                  }}
                  className="group relative flex flex-1 cursor-zoom-in overflow-hidden rounded-[40px]"
                >
                  <Image
                    src={itm?.image_link?.trim() ?? ""}
                    alt={itm.name}
                    width={1000}
                    height={1000}
                    className="h-full w-full transform cursor-pointer rounded-[40px] object-cover transition-transform duration-300 group-hover:scale-110"
                  />

                  <div className="absolute inset-0 z-30 hidden items-center justify-center group-hover:flex">
                    <Search className="h-10 w-10 text-secondary" />
                  </div>

                  <div
                    className="pointer-events-none absolute inset-x-0 -bottom-4 z-30 h-4/5 transition-all duration-300 ease-linear group-hover:h-full"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.9) 100%)",
                    }}
                  ></div>
                  <div className="absolute inset-0 z-50 flex flex-col justify-end gap-1 p-4">
                    <h5 className="text-xl text-secondary lg:text-3xl ltr:font-playfair">
                      {itm.name}
                    </h5>
                    <span className="text-sm lg:text-base ltr:font-opensans">
                      {itm?.title} - {itm.year}
                    </span>
                  </div>
                </div>
                {itm.description && (
                  <div className="flex-[0.3] flex-shrink-0 px-4 text-sm text-secondary/60">
                    <p>{itm?.description}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}

function MobileCards({
  data,
  setIndex,
}: {
  data: HonoreesData[];
  setIndex: (index: number) => void;
}) {
  return (
    <div className="relative flex h-full flex-wrap gap-6 overflow-hidden">
      {data.map((item, idx) => (
        <div
          key={item.id}
          className={`relative min-w-full flex-1 sm:min-w-[40%] sm:max-w-[49%] lg:min-w-[30%] lg:max-w-[33%]`}
        >
          <div
            className={cn(
              "flex flex-col gap-4 rounded-[40px] bg-foreground p-4 pb-8 sm:h-[500px]",
              item.absolute ? "bottom-0 left-0 right-0 lg:absolute" : "",
              !item.description && "pb-4",
            )}
            style={{ height: `${item.height}px` }}
          >
            <div
              onClick={() => setIndex(idx)}
              className="group relative flex flex-1 cursor-zoom-in overflow-hidden rounded-[40px]"
            >
              <Image
                src={item?.image_link.trim() ?? ""}
                alt={item.name}
                width={1000}
                height={1000}
                className="h-full w-full transform cursor-pointer rounded-[40px] object-cover transition-transform duration-300 group-hover:scale-110"
              />

              <div className="absolute inset-0 z-30 hidden items-center justify-center group-hover:flex">
                <Search className="h-10 w-10 text-secondary" />
              </div>

              <div
                className="pointer-events-none absolute inset-x-0 -bottom-4 z-30 h-4/5 transition-all duration-300 ease-linear group-hover:h-full"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.9) 100%)",
                }}
              ></div>
              <div className="absolute inset-0 z-50 flex flex-col justify-end gap-1 p-4">
                <h5 className="text-xl text-secondary lg:text-3xl ltr:font-playfair">
                  {item.name}
                </h5>
                <span className="text-sm lg:text-base ltr:font-opensans">
                  {item?.title} - {item.year}
                </span>
              </div>
            </div>
            {item.description && (
              <div className="flex-[0.3] flex-shrink-0 px-4 text-sm text-secondary/60">
                <p>{item?.description}</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
